import React from 'react';
import { Link } from 'react-router-dom';
import { Droplets, Gem, Sparkles, BedDouble, Package, PartyPopper, HardHat, Shirt, PawPrint, Check, Star } from 'lucide-react';
import './HomePage.css';

const HomePage = () => {
  const services = [
    { icon: Droplets, name: 'Regular Cleaning', desc: 'Everyday upkeep made easy' },
    { icon: Gem, name: 'Deep Cleaning', desc: 'The details that make it sparkle' },
    { icon: Sparkles, name: 'Standard Maid Service', desc: 'Full reset at a flat rate' },
    { icon: BedDouble, name: 'Maid & Linen Change', desc: 'Hotel-quality refresh' },
    { icon: Package, name: 'Move-In/Move-Out', desc: 'Fresh start or perfect finish' },
    { icon: PartyPopper, name: 'Post-Party Clean-Up', desc: 'Enjoy the party, skip the cleanup' },
    { icon: HardHat, name: 'Post-Construction', desc: 'From dust to dazzling' },
    { icon: Shirt, name: 'Personal Laundry', desc: 'Wash, dry & fold add-on' },
    { icon: PawPrint, name: 'Pet Hair Removal', desc: 'For homes with furry friends' },
  ];

  const benefits = [
    { icon: Check, title: 'Transparent Pricing', desc: 'Know exactly what you pay, no hidden fees' },
    { icon: Check, title: 'Trusted Professionals', desc: 'Background-checked, insured cleaners' },
    { icon: Check, title: 'Flexible Scheduling', desc: 'Same-day, scheduled, or recurring service' },
    { icon: Check, title: 'Satisfaction Guaranteed', desc: '100% happiness or we make it right' },
  ];

  const testimonials = [
    { name: 'Sarah M.', role: 'Homeowner', text: 'Lava Co transformed my Airbnb business. Every turnover is perfect, on time, and the photos they send give me peace of mind.' },
    { name: 'David R.', role: 'Property Manager', text: 'Finally, a cleaning service that gets it. Professional, reliable, and they treat every property like it\'s their own.' },
    { name: 'Jennifer L.', role: 'Los Angeles Resident', text: 'I\'ve tried 5 other services. Lava Co is the only one I trust. They\'re worth every penny.' }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title font-heading">
              Los Angeles Cleaning
              <br />
              <span className="hero-highlight">Done Right</span>
            </h1>
            <p className="hero-subtitle">
              Premium cleaning services for homes, rentals, and businesses.
              <br />
              Transparent pricing. Professional results. Every single time.
            </p>
            <div className="hero-cta">
              <Link to="/book" className="btn btn-primary btn-large">
                Book Your Cleaning
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </Link>
              <Link to="/services" className="btn btn-secondary btn-large">
                View Services
              </Link>
            </div>
            <div className="hero-trust">
              <div className="trust-item">
                <span className="trust-number">500+</span>
                <span className="trust-label">Happy Clients</span>
              </div>
              <div className="trust-item">
                <span className="trust-number">4.9<Star size={16} strokeWidth={2} className="inline-block ml-0.5 align-text-bottom" fill="currentColor" /></span>
                <span className="trust-label">Average Rating</span>
              </div>
              <div className="trust-item">
                <span className="trust-number">100%</span>
                <span className="trust-label">Satisfaction</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-overview">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title font-heading">Our Services</h2>
            <p className="section-subtitle">Whatever you need, we've got you covered</p>
          </div>
          <div className="services-grid">
            {services.map((service, idx) => (
              <div key={idx} className="service-card">
                <div className="service-icon"><service.icon size={24} strokeWidth={1.5} /></div>
                <h3 className="service-name">{service.name}</h3>
                <p className="service-desc">{service.desc}</p>
              </div>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/services" className="btn btn-primary">
              See All Services & Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="benefits">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title font-heading">Why Lava Co?</h2>
            <p className="section-subtitle">The cleaning service Los Angeles trusts</p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="benefit-card">
                <div className="benefit-icon"><benefit.icon size={20} strokeWidth={2} /></div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-desc">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title font-heading">What Our Clients Say</h2>
            <p className="section-subtitle">Don't just take our word for it</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="testimonial-card">
                <div className="testimonial-stars" style={{display: 'flex', gap: '2px'}}>{[1,2,3,4,5].map(i => <Star key={i} size={14} strokeWidth={1.5} fill="currentColor" />)}</div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <h2 className="cta-title font-heading">Ready for a spotless space?</h2>
          <p className="cta-subtitle">Book online in under 5 minutes. No commitment required.</p>
          <Link to="/book" className="btn btn-primary btn-large">
            Get Your Free Quote
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
